import React, { useEffect, useRef } from 'react';
import { useStore } from '../../store';
import { IntakeTabValidationKey } from '../../store/slices/intakeSlice';
import { useShallow } from 'zustand/react/shallow';
import { Controller } from 'react-hook-form';
import type {
  Control,
  UseFormRegister,
  UseFormSetValue,
  FieldErrors,
} from 'react-hook-form';
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardHeader,
  CardContent,
  FormSelect,
  Input,
  TooltipProvider,
  VirtualizedFormSelect,
} from '@roger/ui-components';
import { AlertTriangle, Loader2, RotateCw } from 'lucide-react';
import { DateField } from '../FormFields';
import { ClinicianSelect } from '../clinician/ClinicianSelect';
import { getDayAfterDateStr } from '../../utils/dateUtils';
import { useIntake } from '../../hooks/useIntake';
import { useSchedulingStepForm } from '../../hooks/useCreatePatientForm/useSchedulingStepForm';
import { useSchedulingClinicData } from '../../hooks/useCreatePatientForm/useSchedulingClinicData';
import {
  getMinEpisodeStartDateForMatchedPatient,
  getMinSocDateForMatchedPatient,
} from '../../hooks/useCreatePatientForm/useFormInitialization';
import { KinnserSchedulingStepFormContent } from '../../pages/CreatePatientForm/KinnserSchedulingStepFormContent';
import { RogerSchedulingStepFormContent } from '../../pages/CreatePatientForm/RogerSchedulingStepFormContent';
import { RecSchedulingStepFormContent } from '../../pages/CreatePatientForm/RecSchedulingStepFormContent';
import { SchedulingStepEmptyState } from '../../pages/CreatePatientForm/SchedulingStepEmptyState';
import { canSelectNewAdmissionForSoc } from '../../pages/CreatePatientForm/patientMatchUtils';
import {
  DISCIPLINE_TYPE_OPTIONS,
  getVisitTypePatch,
  isSchedulingPushCompleted,
  type SchedulingEmr,
} from './schedulingVisitType';
import type { SchedulingStepFormData } from '../../schemas/schedulingStepFormSchema';
import { isClinicWrmEnabled } from '../../utils/clinicKeyUtils';
import { useClinicSourceSchedulingEmr } from '../../hooks/useCreatePatientForm/useClinicSourceSchedulingEmr';
import { SchedulingEmrSelectionHeader } from './SchedulingEmrSelectionHeader';
import MarkCompletedButton from './MarkCompletedButton';

interface SchedulingStepProps {
  onStatusChange?: () => void | Promise<void>;
  /** Scroll to a field on this tab when the user clicks a validation link. */
  onScrollToField?: (fieldName: string) => void;
}

/**
 * Visit type / creation type / patient type row shown above the scheduling body for
 * an existing patient. The dropdown options and saved field names vary per EMR.
 */
function VisitCreationRow({
  emr,
  register,
  setValue,
  errors,
  visitType,
  creationType,
  hasMatchedEpisodeDischargeDate,
  socBlockedByOpenAdmission,
  rocBlockedByNoActiveEpisode,
  handleFieldBlur,
  saveToServer,
  isWrmClinic,
}: {
  emr: SchedulingEmr;
  register: UseFormRegister<SchedulingStepFormData>;
  setValue: UseFormSetValue<SchedulingStepFormData>;
  errors: FieldErrors<SchedulingStepFormData>;
  // 'rec' never renders here (REC is new-patient-only; this row is existing-patient),
  // but the shared watch value includes it.
  visitType: 'soc' | 'roc' | 'rec' | null | undefined;
  creationType:
    | 'new_admission'
    | 'new_episode'
    | 'current_episode'
    | null
    | undefined;
  hasMatchedEpisodeDischargeDate: boolean;
  socBlockedByOpenAdmission: boolean;
  rocBlockedByNoActiveEpisode: boolean;
  handleFieldBlur: (e: React.FocusEvent<HTMLElement>) => void;
  saveToServer: (data: Partial<SchedulingStepFormData>) => Promise<void> | void;
  isWrmClinic: boolean;
}) {
  // Visit type / creation type are stored on shared keys across EMRs.
  const visitField = 'kinnser_visit_type' as const;
  const creationField = 'kinnser_creation_type' as const;

  // SOC stays selectable even when the matched patient has an open admission —
  // the block surfaces at the creation-type step (Kinnser won't create a new
  // admission until the prior one is discharged/inactivated), where
  // "New admission" is disabled and the alert below explains why.
  const openAdmissionReason =
    'To create a new admission, you must first inactivate or discharge the latest admission in Kinnser.';
  // ROC resumes a current episode; with no active episode there is nothing to resume.
  const rocUnavailableReason =
    'ROC is unavailable because this patient has no active episode to resume.';
  const visitTypeOptions = [
    { value: 'soc' as const, label: 'SOC' },
    {
      value: 'roc' as const,
      label: 'ROC',
      disabled: rocBlockedByNoActiveEpisode,
      disabledReason: rocBlockedByNoActiveEpisode
        ? rocUnavailableReason
        : undefined,
    },
  ];

  // Only warn about the open admission once the user is actually trying to
  // create a SOC — ROC and the unselected state shouldn't surface it.
  const showOpenAdmissionAlert =
    socBlockedByOpenAdmission && visitType === 'soc';

  // "New admission" is allowed when no open admission blocks SOC and either the matched
  // episode is discharged or the patient has no active episode at all — see
  // canSelectNewAdmissionForSoc.
  const canSelectNewAdmission = canSelectNewAdmissionForSoc(
    hasMatchedEpisodeDischargeDate,
    socBlockedByOpenAdmission,
    rocBlockedByNoActiveEpisode
  );

  // SOC for Kinnser (WRM and non-WRM): only "New admission" — same as non-WRM Kinnser scheduling.
  const creationOptions =
    visitType === 'soc'
      ? [
          {
            value: 'new_admission' as const,
            label: 'New admission',
            disabled: !canSelectNewAdmission,
            disabledReason:
              'Cannot select because admission is active or has not been discharged.',
          },
        ]
      : visitType === 'roc'
        ? [{ value: 'current_episode' as const, label: 'Current episode' }]
        : [];

  const visitTypeHint =
    isWrmClinic && visitType === 'roc'
      ? 'Pushing not available for ROC.'
      : isWrmClinic && visitType === 'soc' && creationType === 'new_admission'
        ? 'Creates admission + episode without a visit'
        : null;

  return (
    <div className='mb-4 pb-4 border-b border-gray-200 flex flex-col gap-3'>
      {showOpenAdmissionAlert && (
        <Alert className='py-2 px-3 bg-amber-50 border-amber-200'>
          <div className='flex items-center gap-2'>
            <AlertTriangle className='h-4 w-4 text-amber-600' />
            <AlertDescription className='text-amber-800 text-xs'>
              {openAdmissionReason}
            </AlertDescription>
          </div>
        </Alert>
      )}
      <div className='flex items-center gap-6 flex-wrap'>
        <div className='flex flex-col gap-1'>
          <FormSelect
            label='Visit type'
            placeholder='Select visit type...'
            options={visitTypeOptions}
            error={errors[visitField]?.message}
            {...register(visitField, {
              onBlur: handleFieldBlur,
              onChange: async e => {
                const v = e.target.value;
                if (v !== 'soc' && v !== 'roc') return;
                const patch = getVisitTypePatch(emr, v, canSelectNewAdmission);
                (
                  Object.keys(patch) as Array<keyof SchedulingStepFormData>
                ).forEach(key => {
                  setValue(
                    key,
                    patch[
                      key
                    ] as SchedulingStepFormData[keyof SchedulingStepFormData],
                    { shouldDirty: true }
                  );
                });
                await saveToServer(patch);
              },
            })}
          />
          {visitTypeHint && (
            <p className='text-xs text-gray-700'>{visitTypeHint}</p>
          )}
        </div>
        <div className='flex items-center gap-2'>
          <FormSelect
            label='Creation type'
            placeholder='Select creation type...'
            options={creationOptions}
            error={errors[creationField]?.message}
            {...register(creationField, {
              onBlur: handleFieldBlur,
              onChange: async e => {
                let v:
                  | 'new_admission'
                  | 'new_episode'
                  | 'current_episode'
                  | null =
                  e.target.value === 'new_admission' ||
                  e.target.value === 'new_episode' ||
                  e.target.value === 'current_episode'
                    ? e.target.value
                    : null;
                if (v === 'new_admission' && !canSelectNewAdmission) {
                  v = null;
                }
                setValue(creationField, v, { shouldDirty: true });
                if (v) await saveToServer({ [creationField]: v });
              },
            })}
          />
        </div>
        <div className='flex flex-col gap-1.5'>
          <label className='text-sm font-medium text-gray-700'>
            Patient type
          </label>
          <Input
            value='Existing'
            disabled
            readOnly
            inputSize='default'
            className='w-32 bg-gray-50'
          />
        </div>
      </div>
    </div>
  );
}

/**
 * ROC date picker rendered inline under the visit/creation row for either EMR.
 * Clamps values below `minRocDateStr` up to that minimum.
 */
function RocDateBlock({
  htmlFor,
  control,
  errors,
  minRocDateStr,
  minRocDate,
  maxRocDateStr,
  maxRocDate,
  saveToServer,
  children,
}: {
  htmlFor: string;
  control: Control<SchedulingStepFormData>;
  errors: FieldErrors<SchedulingStepFormData>;
  minRocDateStr: string | null;
  minRocDate: Date | undefined;
  maxRocDateStr: string | null;
  maxRocDate: Date | undefined;
  saveToServer: (data: Partial<SchedulingStepFormData>) => Promise<void> | void;
  children?: React.ReactNode;
}) {
  return (
    <div className='flex flex-col gap-6'>
      <div className='flex flex-wrap items-start gap-3'>
        <div className='w-44'>
          <Controller
            name='roc_date'
            control={control}
            render={({ field: { value, onChange, onBlur } }) => (
              <DateField
                label='ROC date'
                value={value}
                htmlFor={htmlFor}
                required={true}
                disabled={false}
                error={errors.roc_date?.message}
                onChange={val => {
                  const norm =
                    val && typeof val === 'string' ? val.split('T')[0] : null;
                  // Clamp below the episode start floor and above the episode end ceiling.
                  let effective = val;
                  if (norm && minRocDateStr && norm < minRocDateStr) {
                    effective = minRocDateStr;
                  } else if (norm && maxRocDateStr && norm > maxRocDateStr) {
                    effective = maxRocDateStr;
                  }
                  onChange(effective);
                  if (effective) {
                    void saveToServer({ roc_date: effective });
                  }
                }}
                onBlur={onBlur}
                minDate={minRocDate}
                maxDate={maxRocDate}
                yearRange={{
                  fromYear: new Date().getFullYear() - 1,
                  toYear: new Date().getFullYear() + 1,
                }}
              />
            )}
          />
        </div>
        {children}
      </div>
    </div>
  );
}

/**
 * Standalone "Refresh from Kinnser" action shown beneath the date/clinician selection.
 * Re-runs the Kinnser match for the already-matched patient and re-persists the
 * episode-derived fields (SOC/ROC date bounds, open-admission block). Always available
 * for Kinnser-matched patients so dates can be re-synced after changes in Kinnser — not
 * only when SOC is blocked.
 */
function RefreshFromKinnserButton({
  onRefresh,
  isRefreshing,
}: {
  onRefresh: () => void | Promise<void>;
  isRefreshing?: boolean;
}) {
  return (
    <div className='flex flex-col gap-1'>
      <Button
        type='button'
        variant='outline'
        size='sm'
        className='gap-1.5 self-start'
        disabled={isRefreshing}
        onClick={() => void onRefresh()}
      >
        {isRefreshing ? (
          <Loader2 className='h-3.5 w-3.5 animate-spin' />
        ) : (
          <RotateCw className='h-3.5 w-3.5' />
        )}
        {isRefreshing ? 'Refreshing…' : 'Refresh from Kinnser'}
      </Button>
      <p className='text-xs text-gray-700'>
        Pull the latest episode dates and admission status from Kinnser.
      </p>
    </div>
  );
}

/**
 * Inner content that holds the form. Keyed by clinic data availability so the form
 * remounts when clinicData loads and gets a resolver that includes clinic-conditional
 * rules (e.g. secondary scheduler required). Otherwise validation errors don't show
 * until the user switches tabs and comes back.
 */
const SchedulingStepContent: React.FC<SchedulingStepProps> = ({
  onStatusChange,
  onScrollToField,
}) => {
  const { user, agencyClinics } = useStore(
    useShallow(state => ({
      user: state.user,
      agencyClinics: state.agencyClinics,
    }))
  );

  const {
    // Form
    control,
    register,
    errors,
    setValue,
    getValues,
    handleFieldBlur,

    // Watched values
    pushToKinnser,
    rogerCreateEpisode,
    visitType,
    creationType,

    // Derived intake/matching state
    isWrmClinic,
    isExistingPatient,
    hasMatchedEpisodeDischargeDate,
    patientMatchSource,
    socBlockedByOpenAdmission,
    rocBlockedByNoActiveEpisode,
    canRefreshFromKinnser,
    refreshFromKinnser,
    isRefreshingMatch,
    minRocDateStr,
    minRocDate,
    maxRocDateStr,
    maxRocDate,

    // Save status
    saveStatus,
    saveToServer,

    // Clinics (Kinnser path)
    clinicData,
    loadingClinicData,

    // Roger UserClinic-based data
    availableClinicians,
    loadingRogerClinicians,

    // Intake data
    selectedIntake,

    // Form validation (synced to store so Push to EMR can block onboarding when invalid)
    isValid,
  } = useSchedulingStepForm();

  const setIntakeTabValid = useStore(state => state.setIntakeTabValid);

  if (!selectedIntake) {
    return (
      <div className='h-screen flex items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-gray-600' />
      </div>
    );
  }

  const isNewPatient = selectedIntake.is_new_patient;
  const isNewPatientSet = isNewPatient === true || isNewPatient === false;
  const allowSelectExistingPatient =
    user?.agency?.is_kinnser_enabled === true ||
    user?.agency?.is_rogeremr_enabled === true;
  /** Existing patient matched only in Roger → cannot schedule in Kinnser. */
  const scheduleKinnserBlockedByMatch =
    isExistingPatient && patientMatchSource === 'roger_emr';
  /** Existing patient matched only in Kinnser → cannot schedule in Roger. */
  const scheduleRogerBlockedByMatch =
    isExistingPatient && patientMatchSource === 'kinnser';

  const {
    schedulingEmr,
    showRogerSchedulingOption,
    showKinnserSchedulingOption,
    schedulingSelectionLocked,
  } = useClinicSourceSchedulingEmr({
    agencyClinics,
    agencyClinicId: selectedIntake?.agency_clinic_id,
    isNewPatient,
    intakeSchedulingFlags: {
      roger_create_episode: selectedIntake?.roger_create_episode,
      kinnser_create_episode: selectedIntake?.kinnser_create_episode,
      push_to_roger: selectedIntake?.push_to_roger,
      push_to_kinnser: selectedIntake?.push_to_kinnser,
    },
    rogerAgencyEnabled: user?.agency?.is_rogeremr_enabled === true,
    kinnserAgencyEnabled: user?.agency?.is_kinnser_enabled === true,
    scheduleRogerBlockedByMatch,
    scheduleKinnserBlockedByMatch,
    getValues,
    setValue,
    saveToServer,
  });

  /** Extraction has not confirmed new vs existing but user chose Kinnser and/or Roger scheduling. */
  const schedulingNeedsPatientConfirmation =
    allowSelectExistingPatient &&
    !isNewPatientSet &&
    (pushToKinnser || rogerCreateEpisode);

  // For existing patients, minimum episode start date is after prior episode's start date (and SOC/discharge).
  const minEpisodeStartDate = isExistingPatient
    ? getMinEpisodeStartDateForMatchedPatient(
        selectedIntake as unknown as Parameters<
          typeof getMinEpisodeStartDateForMatchedPatient
        >[0]
      )
    : null;

  // For existing patients with SOC visit type, minimum SOC date is after prior discharge/start of care.
  const minSocDateStr = isExistingPatient
    ? getMinSocDateForMatchedPatient(
        selectedIntake as unknown as Parameters<
          typeof getMinSocDateForMatchedPatient
        >[0]
      )
    : null;

  const matchedStartOfCareRaw = (
    selectedIntake as { kinnser_matched_start_of_care?: string | null }
  )?.kinnser_matched_start_of_care;
  const matchedStartOfCareStr =
    matchedStartOfCareRaw && typeof matchedStartOfCareRaw === 'string'
      ? matchedStartOfCareRaw.split('T')[0]
      : null;
  const matchedEpisodeEndRaw = (
    selectedIntake as { kinnser_matched_episode_end_date?: string | null }
  )?.kinnser_matched_episode_end_date;
  const matchedEpisodeEndStr =
    matchedEpisodeEndRaw && typeof matchedEpisodeEndRaw === 'string'
      ? matchedEpisodeEndRaw.split('T')[0]
      : null;
  const lockedEpisodeStartDateStr = getDayAfterDateStr(
    matchedEpisodeEndStr ?? undefined
  );
  /** SOC + new admission: episode start uses SOC min, not day-after prior episode end. */
  const minEpisodeStartForNewAdmission =
    creationType === 'new_admission'
      ? (minSocDateStr ?? minEpisodeStartDate)
      : minEpisodeStartDate;

  let tabIsValid: boolean;
  if (schedulingNeedsPatientConfirmation) {
    tabIsValid = false;
  } else if (isExistingPatient) {
    tabIsValid = true;
  } else {
    tabIsValid = isValid;
  }
  const lastValidRef = useRef(tabIsValid);
  lastValidRef.current = tabIsValid;

  // Report this tab's validation state so Push to EMR can block onboarding when any tab has errors.
  // On unmount (e.g. switching to Push to EMR), cleanup writes last-known validity so the store stays correct.
  useEffect(() => {
    setIntakeTabValid(IntakeTabValidationKey.Scheduling, tabIsValid);
    return () => {
      setIntakeTabValid(
        IntakeTabValidationKey.Scheduling,
        lastValidRef.current
      );
    };
  }, [setIntakeTabValid, tabIsValid]);

  // Body routing. For each EMR: ROC + creation → inline ROC date; SOC + creation (or
  // new patient) → full SOC form. Existing patient with no visit/creation chosen
  // shows the guide below whichever body (or empty state) is rendered.
  const showKinnserScheduling = pushToKinnser;
  const showRogerScheduling = rogerCreateEpisode && !pushToKinnser;

  const kinnserSocFormReady =
    showKinnserScheduling &&
    (isNewPatient === true ||
      (!allowSelectExistingPatient && !isNewPatientSet) ||
      (isExistingPatient && visitType === 'soc' && Boolean(creationType)));

  const kinnserRocReady =
    showKinnserScheduling && visitType === 'roc' && Boolean(creationType);

  const rogerSocFormReady =
    showRogerScheduling &&
    visitType !== 'rec' &&
    (!isExistingPatient || (visitType === 'soc' && Boolean(creationType)));

  // REC (recert onboarding) is a new-patient flow only.
  const rogerRecFormReady =
    showRogerScheduling && !isExistingPatient && visitType === 'rec';

  const rogerRocReady =
    showRogerScheduling && visitType === 'roc' && Boolean(creationType);

  const showExistingPatientGuide =
    isExistingPatient &&
    (showKinnserScheduling || showRogerScheduling) &&
    (!visitType || !creationType);

  // Refresh action for Kinnser-matched patients. Rendered beneath the date/clinician
  // section for both SOC and ROC — it is the last section except for any guide text,
  // which always stays at the bottom.
  const refreshFromKinnserButton =
    showKinnserScheduling && canRefreshFromKinnser ? (
      <RefreshFromKinnserButton
        onRefresh={refreshFromKinnser}
        isRefreshing={isRefreshingMatch}
      />
    ) : null;

  // Separator below the date/clinician selection. Lives with the date section so it only
  // appears when a date form is actually rendered — when SOC is blocked (no date form),
  // the VisitCreationRow's own divider is the single line above the refresh button.
  const showDateSelectionSeparator =
    kinnserRocReady ||
    rogerRocReady ||
    kinnserSocFormReady ||
    rogerSocFormReady ||
    rogerRecFormReady;

  return (
    <Card className='h-full flex flex-col'>
      <CardHeader className='border-b py-3 px-4'>
        <div className='flex items-center justify-between mb-4'>
          <div className='flex items-center gap-3'>
            {saveStatus === 'saving' && (
              <div className='flex items-center gap-2 text-xs text-gray-700'>
                <Loader2 className='h-3 w-3 animate-spin' />
                <span>Saving...</span>
              </div>
            )}
            {saveStatus === 'saved' && (
              <div className='text-xs text-green-600'>Saved</div>
            )}
            {saveStatus === 'error' && (
              <div className='text-xs text-red-600'>Error saving</div>
            )}
          </div>
          {selectedIntake && (
            <MarkCompletedButton
              intakeId={selectedIntake.id}
              tabKey='scheduling'
              isComplete={
                selectedIntake.tab_statuses?.scheduling === 'complete' ||
                selectedIntake.tab_statuses?.scheduling ===
                  'complete_with_errors'
              }
              onStatusChange={onStatusChange}
              className='ml-2'
            />
          )}
        </div>
        <TooltipProvider delayDuration={300}>
          <SchedulingEmrSelectionHeader
            control={control}
            setValue={(name, value) =>
              setValue(name as keyof SchedulingStepFormData, value)
            }
            saveToServer={saveToServer}
            showRogerSchedulingOption={showRogerSchedulingOption}
            showKinnserSchedulingOption={showKinnserSchedulingOption}
            schedulingSelectionLocked={schedulingSelectionLocked}
            pushCompleted={isSchedulingPushCompleted(selectedIntake.status)}
            scheduleRogerBlockedByMatch={scheduleRogerBlockedByMatch}
            scheduleKinnserBlockedByMatch={scheduleKinnserBlockedByMatch}
            isWrmClinic={isWrmClinic}
            isNewPatient={isNewPatient}
            visitType={visitType}
            recertDate={selectedIntake.recert_date ?? null}
          />
        </TooltipProvider>
      </CardHeader>
      <CardContent className='flex-1 overflow-y-auto p-4 pt-3'>
        {schedulingNeedsPatientConfirmation ? (
          <div className='flex flex-col items-center h-full py-12 px-4 text-center'>
            <div className='bg-amber-50 p-4 rounded-full mb-4'>
              <AlertTriangle className='h-8 w-8 text-amber-600' />
            </div>
            <h3 className='text-lg font-medium text-gray-900'>
              Patient must be confirmed before scheduling
            </h3>
            <p className='text-sm text-gray-700 mt-1 max-w-sm'>
              The patient must be confirmed as a new or existing patient on the
              Extraction tab before scheduling.
            </p>
          </div>
        ) : (
          <div className='flex flex-col gap-4 min-h-0'>
            {isWrmClinic && pushToKinnser && (
              <Alert className='py-2 px-3 bg-blue-50 border-blue-200'>
                <AlertDescription className='text-blue-800 text-xs'>
                  This clinic uses Wellsky Resource Manager (WRM).
                  <br />
                  <br />
                  Though you can select SOC/ROC as the visit type, neither will
                  create a visit in WellSky.
                </AlertDescription>
              </Alert>
            )}
            {isExistingPatient && showKinnserScheduling && (
              <VisitCreationRow
                emr='kinnser'
                register={register}
                setValue={setValue}
                errors={errors}
                visitType={visitType}
                creationType={creationType}
                hasMatchedEpisodeDischargeDate={hasMatchedEpisodeDischargeDate}
                socBlockedByOpenAdmission={socBlockedByOpenAdmission}
                rocBlockedByNoActiveEpisode={rocBlockedByNoActiveEpisode}
                handleFieldBlur={handleFieldBlur}
                saveToServer={saveToServer}
                isWrmClinic={isWrmClinic}
              />
            )}
            {isExistingPatient && showRogerScheduling && (
              <VisitCreationRow
                emr='roger'
                register={register}
                setValue={setValue}
                errors={errors}
                visitType={visitType}
                creationType={creationType}
                hasMatchedEpisodeDischargeDate={hasMatchedEpisodeDischargeDate}
                socBlockedByOpenAdmission={false}
                rocBlockedByNoActiveEpisode={false}
                handleFieldBlur={handleFieldBlur}
                saveToServer={saveToServer}
                isWrmClinic={false}
              />
            )}

            {kinnserRocReady || rogerRocReady ? (
              <RocDateBlock
                htmlFor={kinnserRocReady ? 'rocDate' : 'rogerRocDate'}
                control={control}
                errors={errors}
                minRocDateStr={minRocDateStr}
                minRocDate={minRocDate}
                maxRocDateStr={maxRocDateStr}
                maxRocDate={maxRocDate}
                saveToServer={saveToServer}
              >
                {/* Type of ROC (EMR-1272): picks the RN/PT/OT/ST ROC task/visit
                    variant. Defaults to Nursing, matching the historical
                    always-RN behavior. */}
                <FormSelect
                  label='Type of ROC'
                  placeholder='Select ROC type...'
                  options={DISCIPLINE_TYPE_OPTIONS}
                  className='w-44 [&_label]:text-sm'
                  required={true}
                  error={errors.roc_type?.message}
                  {...register('roc_type', {
                    onBlur: handleFieldBlur,
                    onChange: e => {
                      const v = e.target.value;
                      if (v !== 'SN' && v !== 'PT' && v !== 'OT' && v !== 'ST')
                        return;
                      void saveToServer({ roc_type: v });
                    },
                  })}
                />
                {kinnserRocReady && (
                  <VirtualizedFormSelect
                    label='Clinician'
                    placeholder='Select clinician...'
                    options={clinicData?.clinicians || []}
                    sortOptions
                    className='w-40 [&_label]:text-sm'
                    required={true}
                    disabled={loadingClinicData}
                    error={errors.kinnser_clinician_id?.message}
                    {...register('kinnser_clinician_id', {
                      onBlur: handleFieldBlur,
                      onChange: e => {
                        const clinicianId =
                          typeof e.target.value === 'string' &&
                          e.target.value.trim() !== ''
                            ? e.target.value
                            : null;
                        void saveToServer({
                          kinnser_clinician_id: clinicianId,
                        });
                      },
                    })}
                  />
                )}
                {rogerRocReady && (
                  <ClinicianSelect
                    label='Clinician'
                    placeholder='Select clinician...'
                    clinicians={availableClinicians}
                    role='clinician'
                    className='w-40 [&_label]:text-sm'
                    required={true}
                    disabled={loadingRogerClinicians}
                    error={errors.roger_clinician_id?.message}
                    {...register('roger_clinician_id', {
                      onBlur: handleFieldBlur,
                      onChange: e => {
                        const clinicianId =
                          typeof e.target.value === 'string' &&
                          e.target.value.trim() !== ''
                            ? e.target.value
                            : null;
                        void saveToServer({
                          roger_clinician_id: clinicianId,
                        });
                      },
                    })}
                  />
                )}
              </RocDateBlock>
            ) : kinnserSocFormReady ? (
              <KinnserSchedulingStepFormContent
                control={control}
                register={register}
                errors={errors}
                setValue={setValue}
                getValues={getValues}
                handleFieldBlur={handleFieldBlur}
                saveToServer={saveToServer}
                clinicData={clinicData}
                loadingClinicData={loadingClinicData}
                createResult={null}
                isExistingPatient={isExistingPatient}
                selectedIntake={selectedIntake}
                onScrollToField={onScrollToField}
                kinnserCreationType={
                  creationType ??
                  (isWrmClinic ? 'new_admission' : 'new_episode')
                }
                minEpisodeStartDate={
                  isExistingPatient ? minEpisodeStartForNewAdmission : null
                }
                minSocDate={isExistingPatient ? minSocDateStr : null}
                socDateLockedToMatchedEpisode={
                  isExistingPatient &&
                  !hasMatchedEpisodeDischargeDate &&
                  creationType === 'new_episode' &&
                  !!matchedStartOfCareStr
                }
                lockedSocDateStr={
                  isExistingPatient ? matchedStartOfCareStr : null
                }
                episodeStartLockedToMatchedEnd={
                  isExistingPatient &&
                  creationType === 'new_episode' &&
                  !!lockedEpisodeStartDateStr
                }
                lockedEpisodeStartDateStr={
                  isExistingPatient ? lockedEpisodeStartDateStr : null
                }
                episodeStartLockedToSoc={
                  isExistingPatient && creationType === 'new_admission'
                }
                hideSocDate={isWrmClinic}
              />
            ) : rogerRecFormReady ? (
              <RecSchedulingStepFormContent
                control={control}
                register={register}
                setValue={setValue}
                errors={errors}
                handleFieldBlur={handleFieldBlur}
                saveToServer={saveToServer}
                rogerClinicians={availableClinicians}
                loadingRogerClinicians={loadingRogerClinicians}
                onScrollToField={onScrollToField}
              />
            ) : rogerSocFormReady ? (
              <RogerSchedulingStepFormContent
                control={control}
                register={register}
                setValue={setValue}
                errors={errors}
                handleFieldBlur={handleFieldBlur}
                saveToServer={saveToServer}
                rogerClinicians={availableClinicians}
                loadingRogerClinicians={loadingRogerClinicians}
                isExistingPatient={isExistingPatient}
                selectedIntake={selectedIntake}
                onScrollToField={onScrollToField}
              />
            ) : !showExistingPatientGuide &&
              !showRogerSchedulingOption &&
              !showKinnserSchedulingOption ? (
              <SchedulingStepEmptyState
                title={
                  schedulingEmr
                    ? 'Scheduling unavailable'
                    : 'Select a clinic to schedule'
                }
                description={
                  schedulingEmr
                    ? 'This clinic’s EMR is not available for scheduling. Check agency EMR settings or patient match, or choose a different clinic.'
                    : 'Choose a clinic on the Extraction tab. Scheduling will open in the correct EMR for that clinic.'
                }
              />
            ) : null}

            {showDateSelectionSeparator && (
              <div className='border-t border-gray-200' />
            )}

            {refreshFromKinnserButton}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const SchedulingStep: React.FC<SchedulingStepProps> = ({
  onStatusChange,
  onScrollToField,
}) => {
  const { selectedIntake } = useIntake();
  const user = useStore(state => state.user);
  const agencyClinics = useStore(state => state.agencyClinics);
  const setIntakeTabValid = useStore(state => state.setIntakeTabValid);
  const { clinicData, loadingClinicData } = useSchedulingClinicData({
    agencySystemName: user?.agency?.system_name,
    kinnserClinicId: selectedIntake?.kinnser_clinic_id,
  });

  // While clinic data is loading the form is not mounted; don't leave Scheduling as "valid" from on-load.
  useEffect(() => {
    if (loadingClinicData) {
      setIntakeTabValid(IntakeTabValidationKey.Scheduling, null);
    }
  }, [loadingClinicData, setIntakeTabValid]);

  if (!selectedIntake) {
    return (
      <div className='h-screen flex items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin text-gray-600' />
      </div>
    );
  }

  // Show loading spinner for entire section while clinic info is loading
  if (loadingClinicData) {
    return (
      <div className='h-full flex flex-col'>
        <div className='h-full flex items-center justify-center'>
          <div className='flex flex-col items-center gap-4'>
            <Loader2 className='h-8 w-8 animate-spin text-gray-600' />
          </div>
        </div>
      </div>
    );
  }

  // Key by clinic data so the form remounts when clinicData loads and gets the correct
  // resolver (e.g. secondary scheduler required). Ensures validation errors show immediately.
  const isWrmForKey = isClinicWrmEnabled(
    agencyClinics,
    selectedIntake?.agency_clinic_id
  );
  const formKey = `${clinicData != null ? 'loaded' : 'loading'}-${isWrmForKey ? 'wrm' : 'std'}`;
  return (
    <SchedulingStepContent
      key={formKey}
      onStatusChange={onStatusChange}
      onScrollToField={onScrollToField}
    />
  );
};

export default SchedulingStep;
